import React, { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ppProcessorService from '../services/ppProcessorService';
import paymentporterService from '../services/paymentporterService';
import PpDryRunModal from './PpDryRunModal';
import ConfirmModal from './shared/ConfirmModal';
import './PaymentPorterPage.css';
import './PaymentPorterRunBetweenDatesPage.css';

// Run Between Dates — the porter over an ARRIVAL WINDOW instead of today's creations.
//
// WHY IT IS A PAGE AND NOT A DIALOG. STYLE.md: "Do not use modals. Every interaction that would go in a
// modal must be a full page instead", and it names this exact route shape as its example. The reasons
// bite here specifically: this screen holds a table of up to fifty selectable rows with per-row actions,
// it takes real time to load, and an operator who reloads or shares the URL should land back on the same
// window rather than on an empty porter page. The one permitted overlay — the raw dry-run viewer — is
// still an overlay, because it is a viewer rather than a step in the flow.
//
// THREE STEPS: the dates, the rules to search with, then what matches them.
//
// THE RULE STEP IS A SEARCH FILTER, NOT A RUN INSTRUCTION, and the screen says so rather than letting an
// operator assume otherwise. Ticking two of five rules asks "what would these two pick up in this
// window". When the chosen reservations are then processed, pp-resolve-action evaluates EVERY active
// rule in the porter's priority order — exactly as the scheduled job does — so the rule that finally
// acts may not be one that was ticked. Constraining that would be a second definition of "which rule
// applies", and it would drift from the first.
//
// IT REUSES THE SCHEDULED RUN'S OWN MACHINERY, end to end, and that is the point rather than a
// convenience:
//   matching   criteria-preview?matchList=true with an arrival window — the same endpoint, matcher and
//              already-processed detection the Test screen uses, one different base query
//   running    /run with reservationIds — pp-sfn-match already honours a supplied list, so a selection
//              reaches Fetch and Process by exactly the path the nightly job takes
//   dry run    the same synchronous dry-run call and the same PpDryRunModal as the Test screen
// A second implementation of any of those would eventually process a chosen reservation differently
// from the scheduled one, and the difference would surface as a payment that moved on one route and not
// the other.

const PAGE_SIZE = 50;

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (iso, n) => {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
};

export default function PaymentPorterRunBetweenDatesPage() {
    const { ppId } = useParams();
    const navigate = useNavigate();

    const [step, setStep] = useState(1);
    const [from, setFrom] = useState(today());
    const [to, setTo] = useState(plusDays(today(), 7));

    // Active rules, and which of them to search with. Defaults to ALL — the porter's own answer to
    // "what would you pick up", which is what somebody pressing Next without thinking about it means.
    const [rules, setRules] = useState([]);
    const [rulesLoading, setRulesLoading] = useState(false);
    const [chosenRules, setChosenRules] = useState(() => new Set());

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [matched, setMatched] = useState([]);
    const [totalReservations, setTotalReservations] = useState(null);

    const [page, setPage] = useState(0);
    const [selected, setSelected] = useState(() => new Set());
    const [details, setDetails] = useState({});

    const [dryRun, setDryRun] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const [running, setRunning] = useState(false);
    const [runResult, setRunResult] = useState(null);
    const [progress, setProgress] = useState(null);
    const [rowBusy, setRowBusy] = useState({});

    const pageStart = page * PAGE_SIZE;
    const pageRows = matched.slice(pageStart, pageStart + PAGE_SIZE);
    const totalPages = Math.max(1, Math.ceil(matched.length / PAGE_SIZE));

    // Per-page "already processed" status. Fetched a page at a time because each id costs an OHIP detail
    // call — fifty is comfortably inside API Gateway's 29 seconds, five hundred is not.
    const loadPageDetails = useCallback(async (rows) => {
        const ids = rows.map(r => r.reservationId).filter(id => details[id] === undefined);
        if (!ids.length) return;
        try {
            const res = await ppProcessorService.getReservationDetails(ppId, ids);
            setDetails(prev => ({ ...prev, ...(res.data?.details || {}) }));
        } catch (e) {
            // Not fatal: the rows are still runnable, they simply cannot say whether they ran before.
            console.error('[pp-run-between] detail lookup failed', e);
        }
    }, [ppId, details]);

    const toRules = async () => {
        if (!from || !to) { setError('Both dates are required.'); return; }
        if (to < from) { setError('The To date cannot be before the From date.'); return; }
        setError(null); setRulesLoading(true);
        try {
            const res = await paymentporterService.listRules(ppId);
            // { rules: [...] }, not a bare array — the porter page reads it the same way. Active only,
            // because the scheduled job only ever evaluates active rules and this screen must offer the
            // same set it would.
            const list = (res.data?.rules || []).filter(r => Number(r.active) === 1);
            setRules(list);
            // Pre-ticked, not empty. An empty selection would make Next look broken, and "all rules" is
            // what this screen means when nobody narrows it.
            setChosenRules(new Set(list.map(r => r.id)));
            setStep(2);
        } catch (e) {
            setError(e.response?.data?.error?.message || e.response?.data?.message
                || `The rules could not be read — ${e.message}`);
        } finally { setRulesLoading(false); }
    };

    // ONE RULE PER REQUEST, accumulated here.
    //
    // A single call carrying all twenty-four rules over a six-month window 504'd: each rule is a full
    // axis scan of the window, and no arrangement of the code fits that into API Gateway's 29 seconds.
    // Splitting it is not a workaround — it is the only shape that scales, and it buys three things a
    // single call cannot: progress an operator can watch, results that survive one slow rule, and a
    // failure that names the rule it happened on instead of a bare gateway timeout.
    //
    // AGGREGATED THE WAY THE FETCHER AGGREGATES: one row per reservation, first rule wins the row, later
    // rules append their labels. Iterating in the porter's own order (the list arrives sorted by
    // sequence) makes "first" mean the same thing here as it does in the run.
    const search = async () => {
        if (!chosenRules.size) { setError('Choose at least one rule to search with.'); return; }
        const ids = rules.filter(r => chosenRules.has(r.id)).map(r => r.id);

        setLoading(true); setError(null); setRunResult(null);
        setMatched([]); setSelected(new Set()); setPage(0); setDetails({});
        setStep(3);

        const agg = new Map();
        const failed = [];
        try {
            for (let i = 0; i < ids.length; i++) {
                const rule = rules.find(r => r.id === ids[i]);
                setProgress({ done: i, total: ids.length, rule: rule ? rule.name : ids[i] });
                try {
                    const res = await ppProcessorService.getMatchList(ppId, null,
                        { from, to, ruleIds: [ids[i]] });
                    const data = res.data || {};
                    if (data.available === false) {
                        setError(data.reason || 'OHIP is not configured for this porter.');
                        break;
                    }
                    for (const row of (data.matched || [])) {
                        const existing = agg.get(row.reservationId);
                        if (!existing) agg.set(row.reservationId, { ...row });
                        else existing.matchedCriteria = [
                            ...(existing.matchedCriteria || []), ...(row.matchedCriteria || [])];
                    }
                } catch (e) {
                    // One slow or failing rule must not lose the ones that worked. Named, and the loop
                    // carries on — a partial answer an operator can see is worth more than a 504.
                    failed.push(rule ? rule.name : String(ids[i]));
                }
                // Shown as it goes, so a long search is legible rather than a spinner.
                setMatched([...agg.values()]);
            }
            setProgress({ done: ids.length, total: ids.length, rule: null });
            if (failed.length) {
                setError(`${failed.length} rule${failed.length === 1 ? '' : 's'} could not be searched `
                    + `(${failed.join(', ')}). The rest are listed below — narrow the dates and try those again.`);
            }
            const list = [...agg.values()];
            setMatched(list);
            loadPageDetails(list.slice(0, PAGE_SIZE));
        } finally { setLoading(false); }
    };

    const goToPage = (p) => {
        setPage(p);
        loadPageDetails(matched.slice(p * PAGE_SIZE, p * PAGE_SIZE + PAGE_SIZE));
    };

    const toggle = (id) => setSelected(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    // SELECT ALL MEANS EVERY MATCH, NOT THIS PAGE. An operator who ticks the header box and presses Run
    // means the window they searched for; selecting only the fifty in front of them would silently run a
    // fraction of it and report success.
    const allRulesChosen = rules.length > 0 && chosenRules.size === rules.length;
    const allSelected = matched.length > 0 && selected.size === matched.length;
    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(matched.map(r => r.reservationId)));

    const runOne = async (r, { dry } = {}) => {
        setRowBusy(prev => ({ ...prev, [r.reservationId]: true }));
        try {
            if (dry) {
                const label = r.confirmationNum || r.reservationId;
                setDryRun({ reservationId: r.reservationId, label, loading: true, error: null, result: null });
                const res = await ppProcessorService.dryRunReservation(ppId, { reservationId: r.reservationId });
                setDryRun({ reservationId: r.reservationId, label, loading: false, error: null, result: res.data || null });
            } else {
                await ppProcessorService.runSingleReservation(ppId, {
                    reservationId: r.reservationId,
                    confirmationNumber: r.confirmationNum,
                    matchedCriteria: r.matchedCriteria || [],
                });
                setRunResult({ kind: 'single', count: 1, id: r.reservationId });
            }
        } catch (e) {
            const msg = e.response?.data?.error?.message || e.response?.data?.message || 'The run could not be started.';
            if (dry) setDryRun(prev => ({ ...(prev || {}), loading: false, error: msg, result: null }));
            else setError(msg);
        } finally {
            setRowBusy(prev => { const n = { ...prev }; delete n[r.reservationId]; return n; });
        }
    };

    const runMany = async (ids, { dry } = {}) => {
        setRunning(true); setError(null);
        try {
            const res = await ppProcessorService.runSelected(ppId, ids, { dryRun: !!dry });
            setRunResult({ kind: dry ? 'dry-batch' : 'batch', count: ids.length, runUuid: res.data?.runUuid || null });
        } catch (e) {
            setError(e.response?.data?.error?.message || e.response?.data?.message || 'The run could not be started.');
        } finally { setRunning(false); setConfirm(null); }
    };

    const askRun = (ids, dry) => setConfirm({
        title: dry ? 'Dry run these reservations?' : 'Run these reservations?',
        message: dry
            ? `${ids.length} reservation${ids.length === 1 ? '' : 's'} will be evaluated and the result recorded. `
              + 'Nothing is sent to the PMS.'
            : `${ids.length} reservation${ids.length === 1 ? '' : 's'} will be processed exactly as the scheduled `
              + 'job processes them, and payments will be moved in the PMS. This cannot be undone from here.',
        confirmLabel: dry ? 'Dry run' : `Run ${ids.length}`,
        danger: !dry,
        onConfirm: () => runMany(ids, { dry }),
    });

    const selectedIds = matched.filter(r => selected.has(r.reservationId)).map(r => r.reservationId);
    const allIds = matched.map(r => r.reservationId);

    return (
        <div className="acp-page">
            <button className="pp-back-btn" onClick={() => navigate(`/apps/payment-porter/${ppId}`)}>
                ‹ Back to porter
            </button>

            <div className="acp-card">
                <div className="acp-card-header">
                    <h2 className="acp-title">Run between dates</h2>
                    <p className="prb-sub">
                        The scheduled job runs over reservations created today. This runs the same porter over
                        the arrivals in a window you choose.
                    </p>
                </div>

                {/* ── STEP 1 ───────────────────────────────────────────────────── */}
                <div className="prb-step">
                    <div className="prb-step-head">
                        <span className={`prb-step-no${step === 1 ? ' prb-step-on' : ''}`}>1</span>
                        <span className="prb-step-title">Choose the arrival dates</span>
                    </div>

                    <div className="prb-dates">
                        <label className="prb-field">
                            <span className="prb-field-label">From date</span>
                            <input type="date" className="prb-input" value={from}
                                   onChange={e => { setFrom(e.target.value); setStep(1); }} />
                        </label>
                        <label className="prb-field">
                            <span className="prb-field-label">To date</span>
                            <input type="date" className="prb-input" value={to}
                                   onChange={e => { setTo(e.target.value); setStep(1); }} />
                        </label>
                        <button className="pp-run-btn" onClick={toRules} disabled={rulesLoading}>
                            {rulesLoading ? 'Loading rules…' : 'Next'}
                        </button>
                    </div>
                    {error && <div className="pp-run-error">{error}</div>}
                </div>

                {/* ── STEP 2 ───────────────────────────────────────────────────── */}
                {step >= 2 && (
                    <div className="prb-step">
                        <div className="prb-step-head">
                            <span className="prb-step-no prb-step-on">2</span>
                            <span className="prb-step-title">Choose the rules to search with</span>
                        </div>

                        <p className="prb-note">
                            These narrow <strong>which arrivals are found</strong>. When you run them, the
                            porter applies its normal rule priority to each reservation — the rule that
                            ends up acting may not be one ticked here.
                        </p>

                        {rules.length === 0 ? (
                            <p className="prb-empty">This porter has no active rules, so nothing can match.</p>
                        ) : (
                            <>
                                {/* ABOVE THE LIST, not below it. Select all / Clear were under the
                                    rules, which is fine for four and useless for twenty-four — the
                                    control an operator needs before they start ticking was off the
                                    bottom of the screen. Same master-checkbox pattern as the arrivals
                                    table in step 3, including the indeterminate state, so the two
                                    lists on this page behave identically. */}
                                <label className="prb-selectall">
                                    <input
                                        type="checkbox"
                                        checked={allRulesChosen}
                                        ref={el => { if (el) el.indeterminate = chosenRules.size > 0 && !allRulesChosen; }}
                                        onChange={() => setChosenRules(allRulesChosen
                                            ? new Set()
                                            : new Set(rules.map(r => r.id)))}
                                    />
                                    {allRulesChosen ? 'Deselect all' : 'Select all'} {rules.length} rule{rules.length === 1 ? '' : 's'}
                                </label>

                                <div className="prb-rules">
                                    {rules.map(r => (
                                        <label key={r.id} className="prb-rule">
                                            <input
                                                type="checkbox"
                                                checked={chosenRules.has(r.id)}
                                                onChange={() => setChosenRules(prev => {
                                                    const next = new Set(prev);
                                                    if (next.has(r.id)) next.delete(r.id); else next.add(r.id);
                                                    return next;
                                                })}
                                            />
                                            <span className="prb-rule-name">{r.name}</span>
                                            {r.sequence != null && <span className="prb-rule-seq">#{r.sequence}</span>}
                                        </label>
                                    ))}
                                </div>
                                <div className="prb-actions">
                                    <button className="pp-run-btn" onClick={search}
                                            disabled={loading || !chosenRules.size}>
                                        {loading ? 'Searching…' : `Find arrivals (${chosenRules.size} rule${chosenRules.size === 1 ? '' : 's'})`}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* ── STEP 3 ───────────────────────────────────────────────────── */}
                {step === 3 && (
                    <div className="prb-step">
                        <div className="prb-step-head">
                            <span className="prb-step-no prb-step-on">3</span>
                            <span className="prb-step-title">
                                Arrivals between {from} and {to} that match this porter
                            </span>
                        </div>

                        {loading && progress && (
                            <div className="prb-summary">
                                Searching rule {Math.min(progress.done + 1, progress.total)} of {progress.total}
                                {progress.rule ? ` — ${progress.rule}` : ''}… {matched.length} found so far.
                            </div>
                        )}

                        <div className="prb-summary">
                            <strong>{matched.length}</strong> matching arrival{matched.length === 1 ? '' : 's'}
                            {' '}between {from} and {to}
                            {/* The "of N arriving" total is only fetched for a business day — counting
                                every arrival across a long window costs a list call per hundred and was
                                most of what timed the request out. */}
                            {totalReservations != null && <> of <strong>{totalReservations}</strong></>}
                            {matched.length > 0 && <> · {selected.size} selected</>}
                        </div>

                        {matched.length === 0 ? (
                            <p className="prb-empty">
                                {loading
                                    ? 'Searching…'
                                    : 'No arrivals in that window match the rules you chose. Widen the dates, '
                                      + 'tick more rules, or check their profiles and rate codes.'}
                            </p>
                        ) : (
                            <>
                                <div className="prb-actions">
                                    <label className="prb-selectall">
                                        <input type="checkbox" checked={allSelected}
                                               ref={el => { if (el) el.indeterminate = selected.size > 0 && !allSelected; }}
                                               onChange={toggleAll} />
                                        Select all {matched.length}
                                    </label>
                                    <button className="pp-run-btn" disabled={running || !selectedIds.length}
                                            onClick={() => askRun(selectedIds, false)}>
                                        Run {selectedIds.length || ''} selected
                                    </button>
                                    <button className="prb-btn" disabled={running || !selectedIds.length}
                                            onClick={() => askRun(selectedIds, true)}>
                                        Dry run selected
                                    </button>
                                    <button className="pp-run-btn" disabled={running || !allIds.length}
                                            onClick={() => askRun(allIds, false)}>
                                        Run all {allIds.length}
                                    </button>
                                </div>

                                {runResult && (
                                    <div className="prb-ok">
                                        {runResult.kind === 'single'
                                            ? `Queued reservation ${runResult.id}.`
                                            : `${runResult.kind === 'dry-batch' ? 'Dry run' : 'Run'} started for `
                                              + `${runResult.count} reservation${runResult.count === 1 ? '' : 's'}`
                                              + `${runResult.runUuid ? ` — run ${runResult.runUuid}` : ''}. `
                                              + 'Progress is on the Runs tab.'}
                                    </div>
                                )}

                                <table className="prb-table">
                                    <thead>
                                        <tr>
                                            <th />
                                            <th>Confirmation</th>
                                            <th>Guest</th>
                                            <th>Arrival</th>
                                            <th>Rate</th>
                                            <th>Matched on</th>
                                            <th>Status</th>
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pageRows.map(r => {
                                            const d = details[r.reservationId];
                                            const busy = !!rowBusy[r.reservationId];
                                            return (
                                                <tr key={r.reservationId}>
                                                    <td>
                                                        <input type="checkbox"
                                                               checked={selected.has(r.reservationId)}
                                                               onChange={() => toggle(r.reservationId)} />
                                                    </td>
                                                    {/* THE CONFIRMATION NUMBER, which is what a hotel
                                                        quotes and searches on — the header used to say
                                                        "Reservation" over it, which named the wrong
                                                        identifier entirely. The reservation id is the
                                                        internal one the run addresses, kept on the
                                                        title so support can still read it off without
                                                        it competing for the column. */}
                                                    <td className="prb-mono"
                                                        title={r.confirmationNum
                                                            ? `reservation ${r.reservationId}`
                                                            : 'no confirmation number on this reservation — showing its internal id'}>
                                                        {r.confirmationNum || r.reservationId}
                                                    </td>
                                                    <td>{r.guestName || '—'}</td>
                                                    <td>{r.arrivalDate || '—'}</td>
                                                    <td>{r.ratePlanCode || '—'}</td>
                                                    <td className="prb-crit">
                                                        {(r.matchedCriteria || []).join(', ') || '—'}
                                                    </td>
                                                    <td>
                                                        {/* Already-processed is stated because running one again is
                                                            usually a mistake — the porter marks the reservation and
                                                            a second pass is a no-op at best. */}
                                                        {d === undefined ? <span className="prb-dim">checking…</span>
                                                            : d.alreadyProcessed ? <span className="prb-done">already processed</span>
                                                            : d.noVirtualCard ? <span className="prb-warn">no virtual card</span>
                                                            : <span className="prb-dim">ready</span>}
                                                    </td>
                                                    <td className="prb-rowbtns">
                                                        <button className="prb-btn" disabled={busy}
                                                                onClick={() => runOne(r, { dry: true })}>
                                                            Dry run
                                                        </button>
                                                        <button className="pp-run-btn prb-run-sm" disabled={busy}
                                                                onClick={() => runOne(r)}>
                                                            {busy ? '…' : 'Run'}
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                {totalPages > 1 && (
                                    <div className="prb-pager">
                                        <button className="prb-btn" disabled={page === 0}
                                                onClick={() => goToPage(page - 1)}>‹ Previous</button>
                                        <span>
                                            {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, matched.length)} of {matched.length}
                                        </span>
                                        <button className="prb-btn" disabled={page >= totalPages - 1}
                                                onClick={() => goToPage(page + 1)}>Next ›</button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            {dryRun && (
                <PpDryRunModal
                    reservationLabel={dryRun.label}
                    loading={dryRun.loading}
                    error={dryRun.error}
                    result={dryRun.result}
                    onClose={() => setDryRun(null)}
                />
            )}

            {confirm && (
                <ConfirmModal
                    title={confirm.title}
                    message={confirm.message}
                    confirmLabel={confirm.confirmLabel}
                    danger={confirm.danger}
                    onConfirm={confirm.onConfirm}
                    onCancel={() => setConfirm(null)}
                />
            )}
        </div>
    );
}
